import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { AnchorComponent, ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Pagination, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link } from "react-router-dom";
import Swal from 'sweetalert2';
import '../App/modalstyle.css';

export default function PetIndex() {
    const { t } = useContext(TranslatorContext);
    const AuthToken = localStorage.getItem("authToken");

    const [pets, setPets] = useState([]);
    const [breeds, setBreeds] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter states
    const [filters, setFilters] = useState({
        customer_id: '',
        breed_id: '',
        name: '',
        per_page: 10
    });

    // Pagination states
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0
    });

    // Store Modal
    const [storeModal, setStoreModal] = useState(false);
    const [storeForm, setStoreForm] = useState({
        name: '',
        breed_id: '',
        customer_id: '',
        age: '',
        color: '',
        male: '1',
        is_dead: false,
        birth_date: '',
        death_date: '',
        weight: '',
    });
    const [storeErrors, setStoreErrors] = useState({});
    const [storeLoading, setStoreLoading] = useState(false);

    // Update Modal
    const [updateModal, setUpdateModal] = useState(false);
    const [updateForm, setUpdateForm] = useState({
        id: null,
        name: '',
        breed_id: '',
        customer_id: '',
        age: '',
        color: '',
        male: '1',
        is_dead: false,
        birth_date: '',
        death_date: '',
        weight: '',
    });
    const [updateErrors, setUpdateErrors] = useState({});
    const [updateLoading, setUpdateLoading] = useState(false);

    // Toast notification functions
    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
        });
    };

    useEffect(() => {
        fetchPets();
        fetchFilterData();
    }, [filters.customer_id, filters.breed_id, filters.name, filters.per_page, pagination.current_page]);

    const fetchPets = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.current_page,
                per_page: filters.per_page,
                ...(filters.customer_id && { customer_id: filters.customer_id }),
                ...(filters.breed_id && { breed_id: filters.breed_id }),
                ...(filters.name && { name: filters.name })
            });

            const res = await axios.get(`${process.env.REACT_APP_API_URL}/pets?${params}`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            setPets(res.data.pets.data);
            setPagination({
                current_page: res.data.pets.current_page,
                last_page: res.data.pets.last_page,
                per_page: res.data.pets.per_page,
                total: res.data.pets.total
            });
        } catch (err) {
            console.error(err);
            showErrorToast(t('Failed to fetch pets'));
        } finally {
            setLoading(false);
        }
    };

    const fetchFilterData = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/pets`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });
            setBreeds(res.data.breeds);
            setCustomers(res.data.customers);
        } catch (err) {
            console.error(err);
            showErrorToast(t('Failed to load filter data'));
        }
    };

    // Handle filter changes
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    // Clear all filters
    const clearFilters = () => {
        setFilters({
            customer_id: '',
            breed_id: '',
            name: '',
            per_page: 10
        });
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    // Handle page change
    const handlePageChange = (page) => {
        setPagination(prev => ({
            ...prev,
            current_page: page
        }));
    };

    // Handle per page change
    const handlePerPageChange = (e) => {
        const value = parseInt(e.target.value);
        setFilters(prev => ({
            ...prev,
            per_page: value
        }));
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    // ---------------- STORE ----------------
    const openStoreModal = async () => {
        if (!breeds.length || !customers.length) {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/pets`, {
                    headers: { Authorization: `Bearer ${AuthToken}` }
                });
                setBreeds(res.data.breeds);
                setCustomers(res.data.customers);
            } catch (err) {
                console.error(err);
                showErrorToast(t('Failed to load data'));
            }
        }
        setStoreForm({
            name: '',
            breed_id: '',
            customer_id: '',
            age: '',
            color: '',
            male: '1',
            is_dead: false,
            birth_date: '',
            death_date: '',
            weight: '',
        });
        setStoreErrors({});
        setStoreModal(true);
    };

    const handleStoreChange = (e) => {
        const { name, value, type, checked } = e.target;
        setStoreForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (storeErrors[name]) setStoreErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleStoreSubmit = async (e) => {
        e.preventDefault();
        setStoreLoading(true);
        setStoreErrors({});

        const submitData = {
            ...storeForm,
            male: parseInt(storeForm.male)
        };

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/pets`,
                submitData,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );
            if (res.data.success) {
                await fetchPets();
                setStoreModal(false);
                showSuccessToast(t('Pet added successfully!'));
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setStoreErrors(err.response.data.errors || {});
                showErrorToast(t('Please check the form for errors'));
            } else {
                showErrorToast(t('Failed to add pet'));
            }
        } finally {
            setStoreLoading(false);
        }
    };

    // ---------------- UPDATE ----------------
    const openUpdateModal = async (pet) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/pets-edit/${pet.id}`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });
            setUpdateForm({
                id: res.data.pet.id,
                name: res.data.pet.name,
                breed_id: res.data.pet.breed_id,
                customer_id: res.data.pet.customer_id,
                age: res.data.pet.age || '',
                color: res.data.pet.color || '',
                male: res.data.pet.male ? '1' : '0',
                is_dead: res.data.pet.is_dead || false,
                birth_date: res.data.pet.birth_date || '',
                death_date: res.data.pet.death_date || '',
                weight: res.data.pet.weight || ''

            });
            setBreeds(res.data.breeds);
            setCustomers(res.data.customers);
            setUpdateErrors({});
            setUpdateModal(true);
        } catch (err) {
            console.error(err);
            showErrorToast(t('Failed to load pet details'));
        }
    };

    const handleUpdateChange = (e) => {
        const { name, value, type, checked } = e.target;
        setUpdateForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (updateErrors[name]) setUpdateErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setUpdateLoading(true);
        setUpdateErrors({});

        const submitData = {
            ...updateForm,
            male: parseInt(updateForm.male)
        };

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/pets-update/${updateForm.id}`,
                submitData,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );
            if (res.data.success) {
                await fetchPets();
                setUpdateModal(false);
                showSuccessToast(t('Pet updated successfully!'));
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setUpdateErrors(err.response.data.errors || {});
                showErrorToast(t('Please check the form for errors'));
            } else {
                showErrorToast(t('Failed to update pet'));
            }
        } finally {
            setUpdateLoading(false);
        }
    };

    // ---------------- DELETE ----------------
    const confirmDelete = (id) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t("You won't be able to revert this!"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    const res = await axios.delete(`${process.env.REACT_APP_API_URL}/pets/${id}`, {
                        headers: { Authorization: `Bearer ${AuthToken}` }
                    });
                    if (res.data.success) {
                        await fetchPets();
                        showSuccessToast(t('Pet deleted successfully!'));
                        return true;
                    }
                } catch (err) {
                    console.error(err);
                    showErrorToast(t('Failed to delete pet'));
                    return false;
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: t('Deleted!'),
                    text: t('Pet has been deleted.'),
                    icon: 'success',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000
                });
            }
        });
    };

    // Helper function to get gender label
    const getGenderLabel = (male) => {
        return male === 1 ? t('Male') : t('Female');
    };

    // Render Bootstrap Pagination
    const renderBootstrapPagination = () => {
        let items = [];
        let maxVisible = 5;
        let startPage = Math.max(1, pagination.current_page - Math.floor(maxVisible / 2));
        let endPage = Math.min(pagination.last_page, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Previous button
        items.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
            />
        );

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    {1}
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
            }
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.current_page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < pagination.last_page) {
            if (endPage < pagination.last_page - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.last_page}
                    onClick={() => handlePageChange(pagination.last_page)}
                >
                    {pagination.last_page}
                </Pagination.Item>
            );
        }

        // Next button
        items.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
            />
        );

        return items;
    };

    if (loading && !pets.length) return <PageLayout><p>{t("loading")}...</p></PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t('pets')}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('pets')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-header d-flex justify-content-between align-items-center p-3">
                            <h5 className="mc-card-title mb-0">{t('pet list')}</h5>
                            <ButtonComponent className="btn btn-primary d-flex align-items-center" onClick={openStoreModal}>
                                <i className="material-icons">add</i>
                                {t('add pet')}
                            </ButtonComponent>
                        </div>

                        {/* Filters Section */}
                        <div className="p-3 border-bottom">
                            <Row className="align-items-end">
                                <Col md={3} className="mb-3">
                                    <Form.Group>
                                        <Form.Label>{t('customer')}</Form.Label>
                                        <Form.Select
                                            name="customer_id"
                                            value={filters.customer_id}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">{t('all customers')}</option>
                                            {customers.map(customer => (
                                                <option key={customer.id} value={customer.id}>
                                                    {customer.name} {customer.type && `(${customer.type})`}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3} className="mb-3">
                                    <Form.Group>
                                        <Form.Label>{t('breed')}</Form.Label>
                                        <Form.Select
                                            name="breed_id"
                                            value={filters.breed_id}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">{t('all breeds')}</option>
                                            {breeds.map(parent => (
                                                <React.Fragment key={parent.id}>
                                                    <option disabled style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>
                                                        {parent.name} {parent.type && `(${parent.type})`}
                                                    </option>
                                                    {parent.child_breeds?.map(child => (
                                                        <option key={child.id} value={child.id}>
                                                            {"\u00A0\u00A0\u00A0\u00A0"}{child.name} {child.type && `(${child.type})`}
                                                        </option>
                                                    ))}
                                                </React.Fragment>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3} className="mb-3">
                                    <Form.Group>
                                        <Form.Label>{t('pet name')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={filters.name}
                                            onChange={handleFilterChange}
                                            placeholder={t('search by name...')}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2} className="mb-3">
                                    <ButtonComponent
                                        className="btn btn-secondary w-100"
                                        onClick={clearFilters}
                                    >
                                        {t('clear')}
                                    </ButtonComponent>
                                </Col>
                            </Row>
                        </div>

                        <Row>
                            <Col xl={12}>
                                <div className="table-responsive-wrapper">
                                    <Table responsive hover className="table mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-center" style={{ width: '5%' }}>#</th>
                                                <th style={{ width: '12%' }}>{t('name')}</th>
                                                <th style={{ width: '15%' }}>{t('breed')}</th>
                                                <th style={{ width: '15%' }}>{t('customer')}</th>
                                                <th className="text-center" style={{ width: '8%' }}>{t('age')}</th>
                                                <th style={{ width: '10%' }}>{t('color')}</th>
                                                <th className="text-center" style={{ width: '8%' }}>{t('gender')}</th>
                                                <th className="text-center" style={{ width: '8%' }}>{t('Weight')} (kg)</th>
                                                <th className="text-center" style={{ width: '8%' }}>{t('is dead')}</th>
                                                <th className="text-center" style={{ width: '11%' }}>{t('actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pets.length > 0 ? (
                                                pets.map((pet, idx) => (
                                                    <tr key={pet.id}>
                                                        <td className="text-center">
                                                            {((pagination.current_page - 1) * pagination.per_page) + idx + 1}
                                                        </td>
                                                        <td>
                                                            <strong className="text-primary">{pet.name}</strong>
                                                        </td>
                                                        <td>
                                                            {pet.breed?.name || '-'}
                                                            {pet.breed?.type && <span className="text-muted small"> ({pet.breed.type})</span>}
                                                        </td>
                                                        <td>
                                                            {pet.customer?.name || '-'}
                                                            {pet.customer?.type && <span className="text-muted small"> ({pet.customer.type})</span>}
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="badge bg-light text-dark">
                                                                {pet.age || '-'} {pet.age ? 'year' : ''}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                {pet.color && (
                                                                    <div
                                                                        className="rounded-circle"
                                                                        style={{
                                                                            width: '16px',
                                                                            height: '16px',
                                                                            backgroundColor: pet.color.toLowerCase(),
                                                                            border: '1px solid #ddd'
                                                                        }}
                                                                    />
                                                                )}
                                                                {pet.color || '-'}
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={`badge ${pet.male === 1 ? 'bg-info' : 'bg-warning'}`}>
                                                                {getGenderLabel(pet.male)}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <strong>{pet.weight} kg</strong>
                                                        </td>
                                                        <td className="text-center">
                                                            {pet.is_dead ? (
                                                                <span className="badge bg-danger">{t('yes')}</span>
                                                            ) : (
                                                                <span className="badge bg-success">{t('no')}</span>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="mc-table-action justify-content-center">
                                                                <ButtonComponent
                                                                    className="material-icons edit"
                                                                    onClick={() => openUpdateModal(pet)}
                                                                    title={t("Edit")}
                                                                >
                                                                    edit
                                                                </ButtonComponent>
                                                                {/* <ButtonComponent
                                                                    className="material-icons delete"
                                                                    onClick={() => confirmDelete(pet.id)}
                                                                    title={t("Delete")}
                                                                >
                                                                    delete
                                                                </ButtonComponent> */}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-5">
                                                        <div className="empty-state">
                                                            <i className="material-icons" style={{ fontSize: '48px', color: '#ccc' }}>pets</i>
                                                            <p className="mt-2 text-muted">{t('No pets found')}</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>

                        {/* Bootstrap Pagination Section */}
                        {pets.length > 0 && (
                            <div className="p-3 border-top">
                                <Row className="align-items-center">
                                    <Col md={6}>
                                        <div className="text-muted">
                                            {t('Showing')} {((pagination.current_page - 1) * pagination.per_page) + 1}
                                            {' '}{t('to')}{' '}
                                            {Math.min(pagination.current_page * pagination.per_page, pagination.total)}
                                            {' '}{t('of')}{' '} {pagination.total} {t('entries')}
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="d-flex justify-content-end">
                                            <Pagination>
                                                {renderBootstrapPagination()}
                                            </Pagination>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* STORE MODAL */}
            <Modal
                show={storeModal}
                onHide={() => setStoreModal(false)}
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">{t('add pet')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleStoreSubmit}>
                    <Modal.Body className="custom-modal-body">
                        <Row className="mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t('name')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        className="custom-input"
                                        value={storeForm.name}
                                        onChange={handleStoreChange}
                                        isInvalid={!!storeErrors.name}
                                        placeholder={t('enter pet name')}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {storeErrors.name?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t('breed')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        name="breed_id"
                                        className="custom-input"
                                        value={storeForm.breed_id}
                                        onChange={handleStoreChange}
                                        isInvalid={!!storeErrors.breed_id}
                                    >
                                        <option value="">{t('select breed')}</option>
                                        {breeds.map(parent => (
                                            <React.Fragment key={parent.id}>
                                                <option disabled className="fw-bold bg-light">
                                                    {parent.name} {parent.type && `(${parent.type})`}
                                                </option>
                                                {parent.child_breeds?.map(child => (
                                                    <option key={child.id} value={child.id}>
                                                        {"\u00A0\u00A0\u00A0"}{child.name} {child.type && `(${child.type})`}
                                                    </option>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {storeErrors.breed_id?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t('customer')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        name="customer_id"
                                        className="custom-input"
                                        value={storeForm.customer_id}
                                        onChange={handleStoreChange}
                                        isInvalid={!!storeErrors.customer_id}
                                    >
                                        <option value="">{t('select customer')}</option>
                                        {customers.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.name} {c.type && `(${c.type})`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {storeErrors.customer_id?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t('age')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="age"
                                        className="custom-input"
                                        value={storeForm.age}
                                        onChange={handleStoreChange}
                                        placeholder={t('enter pet age')}
                                        min="0"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t('color')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="color"
                                        className="custom-input"
                                        value={storeForm.color}
                                        onChange={handleStoreChange}
                                        placeholder={t('enter pet color')}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t('gender')}</Form.Label>
                                    <Form.Select
                                        name="male"
                                        className="custom-input"
                                        value={storeForm.male}
                                        onChange={handleStoreChange}
                                    >
                                        <option value="1">{t('Male')}</option>
                                        <option value="0">{t('Female')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3 align-items-center">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t('Weight (kg)')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="weight"
                                        className="custom-input"
                                        value={storeForm.weight}
                                        onChange={handleStoreChange}
                                        placeholder={t('enter pet Weight')}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t('birth date')}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="birth_date"
                                        className="custom-input"
                                        value={storeForm.birth_date}
                                        onChange={handleStoreChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>


                        {storeForm.is_dead && (
                            <Row className="mt-3 animate__animated animate__fadeIn">
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold text-danger">{t('death date')}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="death_date"
                                            className="custom-input border-danger"
                                            value={storeForm.death_date}
                                            onChange={handleStoreChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            className="btn btn-secondary px-4"
                            onClick={() => setStoreModal(false)}
                        >
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent
                            className="btn btn-primary px-4"
                            type="submit"
                            disabled={storeLoading}
                        >
                            {storeLoading ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : (
                                <i className="material-icons align-middle me-1" style={{ fontSize: '18px' }}>save</i>
                            )}
                            {t('save')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* UPDATE MODAL */}
            <Modal
                show={updateModal}
                onHide={() => setUpdateModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title>{t('edit pet')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateSubmit}>
                    <Modal.Body className="custom-modal-body">
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('name')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={updateForm.name}
                                        onChange={handleUpdateChange}
                                        isInvalid={!!updateErrors.name}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {updateErrors.name?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('breed')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        name="breed_id"
                                        value={updateForm.breed_id}
                                        onChange={handleUpdateChange}
                                        isInvalid={!!updateErrors.breed_id}
                                    >
                                        <option value="">{t('select breed')}</option>
                                        {breeds.map(parent => (
                                            <React.Fragment key={parent.id}>
                                                <option disabled style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>
                                                    {parent.name} {parent.type && `(${parent.type})`}
                                                </option>
                                                {parent.child_breeds?.map(child => (
                                                    <option key={child.id} value={child.id}>
                                                        {"\u00A0\u00A0\u00A0\u00A0"}{child.name} {child.type && `(${child.type})`}
                                                    </option>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {updateErrors.breed_id?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('customer')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        name="customer_id"
                                        value={updateForm.customer_id}
                                        onChange={handleUpdateChange}
                                        isInvalid={!!updateErrors.customer_id}
                                    >
                                        <option value="">{t('select customer')}</option>
                                        {customers.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.name} {c.type && `(${c.type})`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {updateErrors.customer_id?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('age')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="age"
                                        value={updateForm.age}
                                        onChange={handleUpdateChange}
                                        min="0"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('color')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="color"
                                        value={updateForm.color}
                                        onChange={handleUpdateChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('gender')}</Form.Label>
                                    <Form.Select
                                        name="male"
                                        value={updateForm.male}
                                        onChange={handleUpdateChange}
                                    >
                                        <option value="1">{t('Male')}</option>
                                        <option value="0">{t('Female')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('weight (kg)')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="weight"
                                        value={updateForm.weight}
                                        onChange={handleUpdateChange}
                                        placeholder="Enter pet weight"
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t('birth date')}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="birth_date"
                                        value={updateForm.birth_date}
                                        onChange={handleUpdateChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Check
                                        type="checkbox"
                                        label={t('is dead')}
                                        name="is_dead"
                                        checked={updateForm.is_dead}
                                        onChange={handleUpdateChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {updateForm.is_dead && (
                            <Row className="mb-3">
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>{t('death date')}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="death_date"
                                            value={updateForm.death_date}
                                            onChange={handleUpdateChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                    </Modal.Body>

                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            className="btn btn-secondary"
                            onClick={() => setUpdateModal(false)}
                        >
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent
                            className="btn btn-primary"
                            type="submit"
                            disabled={updateLoading}
                        >
                            {updateLoading && (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            )}
                            {t('update')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}